import React, { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { Link } from 'react-router-dom'
import {
  ArrowPathIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { DbStatusBadge } from './tenant/tenantShared'

const TIERS = ['standard', 'professional', 'enterprise']

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({
    groupId: '',
    name: '',
    tier: 'standard',
    venueId: '',
    venueName: '',
    adminUsername: '',
    adminPassword: '',
    adminName: '',
    adminEmail: '',
    sendWelcomeEmail: false,
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setTenants(Array.isArray(data.tenants) ? data.tenants : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load tenants')
      setTenants([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  const submitProvision = async (e) => {
    e.preventDefault()
    const gid = form.groupId.trim()
    if (!gid || !form.name.trim()) {
      toast.error('GSID and display name are required')
      return
    }
    if (!/^[a-z0-9]{2,16}$/.test(gid)) {
      toast.error('GSID must be 2–16 lowercase letters/numbers')
      return
    }
    const hasAdmin = form.adminUsername.trim() && form.adminPassword
    if (hasAdmin && !form.adminEmail.trim()) {
      toast.error('Admin email is required when creating an org admin user')
      return
    }
    if (form.sendWelcomeEmail && !form.adminEmail.trim()) {
      toast.error('Admin email is required to send a welcome email')
      return
    }
    const body = {
      group: {
        groupId: gid,
        subdomain: gid,
        name: form.name.trim(),
        tier: form.tier,
        status: 'active',
      },
      venues: form.venueId.trim()
        ? [{ venueId: form.venueId.trim(), name: (form.venueName || form.venueId).trim() }]
        : [],
      admin:
        hasAdmin
          ? {
              username: form.adminUsername.trim(),
              password: form.adminPassword,
              name: form.adminName.trim() || form.adminUsername.trim(),
              email: form.adminEmail.trim(),
              send_welcome_email: form.sendWelcomeEmail,
            }
          : undefined,
      createDatabase: true,
    }
    setSubmitting(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants/provision'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)

      if (data.welcomeEmail && !data.welcomeEmail.skipped && !data.welcomeEmail.success) {
        toast.error(`Tenant provisioned, but welcome email failed: ${data.welcomeEmail.error || 'unknown error'}`)
      } else if (data.database?.status === 'ready') {
        toast.success(`Tenant provisioned with dedicated database "${data.database.dbName}"`)
      } else if (data.database?.status === 'error') {
        toast.error(`Tenant saved, but database creation failed: ${data.database?.error || 'see warnings'}`)
      } else {
        toast.success('Tenant provisioned')
      }

      setProvisionOpen(false)
      setForm({
        groupId: '', name: '', tier: 'standard',
        venueId: '', venueName: '', adminUsername: '', adminPassword: '',
        adminName: '', adminEmail: '', sendWelcomeEmail: false,
      })
      await load()
    } catch (err) {
      toast.error(err.message || 'Provision failed')
    } finally {
      setSubmitting(false)
    }
  }

  const tenantsWithDb = tenants.filter((t) => t.db_name)
  const tenantsWithoutDb = tenants.filter((t) => !t.db_name)

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-psg-teal p-3 shadow-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
              <p className="text-sm text-gray-600">
                Select a tenant to manage devices, PSG Gateway, branding, printers, and more. Use Add tenant to provision.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setProvisionOpen((o) => !o)}
              className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white shadow hover:opacity-95"
            >
              {provisionOpen ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
              <PlusIcon className="h-4 w-4" />
              Add tenant
            </button>
          </div>
        </div>

        {provisionOpen && (
          <form
            onSubmit={submitProvision}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-900">Provision tenant</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-gray-700">GSID <span className="font-normal text-gray-400">(Group Short ID)</span></span>
                <input
                  required
                  pattern="[a-z0-9]{2,16}"
                  title="2–16 lowercase letters/numbers (e.g. tsg, mtg, wests)"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.groupId}
                  onChange={(e) => updateField('groupId', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="e.g. tsg, mtg, wests"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Used as subdomain ({form.groupId || '___'}.paysafego.net) and database name</span>
              </label>
              <label className="block text-sm sm:col-span-1">
                <span className="font-medium text-gray-700">Display name</span>
                <input
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g. Twinstars Gaming"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700">Tier</span>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.tier}
                  onChange={(e) => updateField('tier', e.target.value)}
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="border-t border-gray-100 pt-4 grid gap-4 sm:grid-cols-2">
              <p className="text-sm font-medium text-gray-700 sm:col-span-2">First venue (optional)</p>
              <label className="block text-sm">
                <span className="text-gray-600">VSID <span className="font-normal text-gray-400">(Venue Short ID)</span></span>
                <input
                  pattern="[a-z0-9][a-z0-9-]{0,62}[a-z0-9]?"
                  title="Lowercase letters, numbers, hyphens (e.g. diggers, main-floor)"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.venueId}
                  onChange={(e) => updateField('venueId', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g. diggers, leagues"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Venue name</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.venueName}
                  onChange={(e) => updateField('venueName', e.target.value)}
                  placeholder="e.g. Diggers Services Club"
                />
              </label>
            </div>
            <div className="border-t border-gray-100 pt-4 grid gap-4 sm:grid-cols-2">
              <p className="text-sm font-medium text-gray-700 sm:col-span-2">Org admin (optional)</p>
              <label className="block text-sm">
                <span className="text-gray-600">Username</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminUsername}
                  onChange={(e) => updateField('adminUsername', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Password</span>
                <input
                  type="password"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminPassword}
                  onChange={(e) => updateField('adminPassword', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Name</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminName}
                  onChange={(e) => updateField('adminName', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Email <span className="text-red-600">*</span></span>
                <input
                  type="email"
                  required={Boolean(form.adminUsername.trim() && form.adminPassword)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminEmail}
                  onChange={(e) => updateField('adminEmail', e.target.value)}
                />
              </label>
              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.sendWelcomeEmail}
                  onChange={(e) => updateField('sendWelcomeEmail', e.target.checked)}
                />
                <span className="text-gray-700">Send welcome email with login details</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setProvisionOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {submitting ? 'Provisioning…' : 'Provision'}
              </button>
            </div>
          </form>
        )}

        {!loading && tenants.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total tenants</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{tenants.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Dedicated DBs</p>
              <p className="mt-1 text-2xl font-bold text-emerald-600">{tenantsWithDb.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Shared DB</p>
              <p className="mt-1 text-2xl font-bold text-gray-600">{tenantsWithoutDb.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">DB errors</p>
              <p className="mt-1 text-2xl font-bold text-red-600">
                {tenants.filter((t) => t.db_status === 'error').length}
              </p>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">GSID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Tier</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Database</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Venues</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Users</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                  </tr>
                )}
                {!loading && tenants.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No tenants found</td>
                  </tr>
                )}
                {!loading &&
                  tenants.map((t) => (
                    <tr key={t.group_id} className="hover:bg-gray-50/80">
                      <td className="px-4 py-3 font-mono text-psg-teal font-semibold">{t.group_id}</td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/tenants/${encodeURIComponent(t.group_id)}`}
                          className="text-gray-900 font-medium hover:text-teal-800"
                        >
                          {t.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          t.tier === 'enterprise' ? 'bg-purple-50 text-purple-700'
                          : t.tier === 'professional' ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {t.tier || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          t.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                          : t.status === 'trial' ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                        }`}>
                          {t.status || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <DbStatusBadge tenant={t} />
                          {t.db_name && (
                            <span className="text-[11px] text-gray-400 font-mono truncate max-w-[140px]" title={t.db_name}>
                              {t.db_name}
                            </span>
                          )}
                          {t.db_schema_version > 0 && (
                            <span className="text-[11px] text-gray-400">v{t.db_schema_version}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">{t.venue_count ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">{t.user_count ?? '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/tenants/${encodeURIComponent(t.group_id)}`}
                          className="inline-flex items-center gap-1 rounded-md border border-teal-600 bg-teal-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-800"
                        >
                          Manage tenant
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
